import { create } from 'zustand'
import type { Product } from '../types'
import { supabase } from '../lib/supabase'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

async function apiFetch(path: string, options: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || res.statusText)
  }

  if (res.status === 204) return null
  return res.json()
}

function mapProduct(row: any): Product {
  return {
    id: row.id,
    sku: row.sku,
    custom_sku: row.custom_sku ?? null,
    name: row.name,
    description: row.description,
    price: Number(row.price),
    unit: row.unit,
    quantity: row.quantity,
    status: row.status,
    category_id: row.category_id ?? null,
    category: row.category,           // ישן — לתאימות
    images: row.images ?? [],
    supplier_id: row.supplier_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

interface ProductStore {
  products: Product[]
  isLoading: boolean
  error: string | null
  fetchProducts: () => Promise<void>
  fetchAdminProducts: () => Promise<void>
  addProduct: (product: Omit<Product, 'id' | 'sku' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateProduct: (id: string, updates: Partial<Omit<Product, 'id' | 'sku' | 'createdAt' | 'updatedAt'>>) => Promise<void>
  deleteProduct: (id: string) => Promise<void>
  getByCategory: (category: Product['category']) => Product[]
}

export const useProductStore = create<ProductStore>((set, get) => ({
  products: [],
  isLoading: false,
  error: null,

  fetchProducts: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await apiFetch(`/api/products`)
      set({ products: (data ?? []).map(mapProduct), isLoading: false })
    } catch (e: any) {
      set({ error: e.message, isLoading: false })
    }
  },

  fetchAdminProducts: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await apiFetch(`/api/products/admin`)
      set({ products: (data ?? []).map(mapProduct), isLoading: false })
    } catch (e: any) {
      set({ error: e.message, isLoading: false })
    }
  },

  addProduct: async (product) => {
    try {
      const data = await apiFetch(`/api/products`, {
        method: 'POST',
        body: JSON.stringify({
          name: product.name,
          description: product.description,
          price: product.price,
          unit: product.unit,
          quantity: product.quantity,
          status: product.status,
          category_id: product.category_id ?? null,
          images: product.images ?? [],
          supplier_id: product.supplier_id,
          custom_sku: product.custom_sku ?? null,
        })
      })
      set(state => ({ products: [...state.products, mapProduct(data)] }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  updateProduct: async (id, updates) => {
    try {
      const data = await apiFetch(`/api/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      set(state => ({
        products: state.products.map(p => p.id === id ? mapProduct(data) : p)
      }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  deleteProduct: async (id) => {
    try {
      await apiFetch(`/api/products/${id}`, { method: 'DELETE' })
      set(state => ({ products: state.products.filter(p => p.id !== id) }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  getByCategory: (category) =>
    get().products.filter(p => p.category === category),
})) 